import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AdminLayout from "@/layouts/admin-layout"
import type { AdminReportsIndex } from "@/types/serializers"

type Props = AdminReportsIndex

const STATUS_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  revisado: "Revisado",
  resuelto: "Resuelto",
}

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  acoso: "Acoso",
  contenido_inapropiado: "Contenido inapropiado",
  perfil_falso: "Perfil falso",
  otro: "Otro",
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pendiente":
      return (
        <Badge variant="destructive">{STATUS_LABELS[status] || status}</Badge>
      )
    case "revisado":
      return (
        <Badge className="border-orange-400/30 bg-orange-500/20 text-orange-400">
          {STATUS_LABELS[status] || status}
        </Badge>
      )
    case "resuelto":
      return (
        <Badge variant="outline" className="border-green-400/30 text-green-400">
          {STATUS_LABELS[status] || status}
        </Badge>
      )
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

export default function AdminReportsIndex({
  reports,
  pagination,
  filters,
  pending_count,
}: Props) {
  const [status, setStatus] = useState(filters.status ?? "")
  const [reason, setReason] = useState(filters.reason ?? "")

  function handleStatusFilter(value: string) {
    const newStatus = value === "all" ? "" : value
    setStatus(newStatus)
    router.get(
      "/admin/reports",
      { status: newStatus, reason },
      { preserveState: true },
    )
  }

  function handleReasonFilter(value: string) {
    const newReason = value === "all" ? "" : value
    setReason(newReason)
    router.get(
      "/admin/reports",
      { status, reason: newReason },
      { preserveState: true },
    )
  }

  return (
    <AdminLayout>
      <Head title="Reportes | Admin KrownFT" />

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-foreground text-2xl font-bold">Reportes</h1>
          {pending_count > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-2 text-xs font-bold text-white">
              {pending_count}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Select value={status || "all"} onValueChange={handleStatusFilter}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="revisado">Revisado</SelectItem>
              <SelectItem value="resuelto">Resuelto</SelectItem>
            </SelectContent>
          </Select>
          <Select value={reason || "all"} onValueChange={handleReasonFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Motivo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los motivos</SelectItem>
              <SelectItem value="spam">Spam</SelectItem>
              <SelectItem value="acoso">Acoso</SelectItem>
              <SelectItem value="contenido_inapropiado">
                Contenido inapropiado
              </SelectItem>
              <SelectItem value="perfil_falso">Perfil falso</SelectItem>
              <SelectItem value="otro">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border-border rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reportado por</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-muted-foreground py-8 text-center"
                  >
                    No hay reportes
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <p className="text-foreground font-medium">
                        {report.reporter.full_name}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {(report.reporter as { email?: string }).email}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {REASON_LABELS[report.reason] || report.reason}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {report.reportable_type}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={report.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(report.created_at).toLocaleDateString("es-AR")}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        href={`/admin/reports/${report.id}`}
                        className="text-krown-300 text-sm font-medium hover:underline"
                      >
                        Ver
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() =>
                    router.get(
                      "/admin/reports",
                      { status, reason, page },
                      { preserveState: true },
                    )
                  }
                  className={`rounded-lg px-3 py-1 text-sm ${
                    page === pagination.page
                      ? "bg-krown-500 text-white"
                      : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {page}
                </button>
              ),
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
